import { FeatureFlag } from '@ps/web-core-enums';
import {
  TrackingContext,
  type TrackingContextValue,
  sportsCompetition,
  sportsEvent,
  sportsMarket,
  sportsSport
} from '@ps/web-shared-analytics';
import { Link, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { RootState } from '@swf/core/store';
import { Icon, Selection } from '@swf/sports/components';
import Badges from '@swf/sports/components/Badges/Badges';
import EventStatus from '@swf/sports/components/EventStatus/EventStatus';
import useEventStatus from '@swf/sports/components/EventStatus/hooks/useEventStatus';
import EventStructuredData from '@swf/sports/components/EventStructuredData/EventStructuredData';
import Scoreboard from '@swf/sports/components/Scoreboard/Scoreboard';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import { getIsEventTypeScoreboardEnabled } from '@swf/sports/utils/getIsEventTypeScoreboardEnabled';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';
import mergeWithTrackingContextValue from '@swf/sports/utils/mergeWithTrackingContextValue';

import {
  TXTWCL_More_markets,
  TXTWCL_Star_Sub_selections,
  TXTWCL_odds_boost_selections
} from './Event.messages.json';
import styles from './Event.scss';
import Participants from './Participants/Participants';
import type { Event as EventType } from './types';

const appendTrackingEntities = (
  trackingContext: TrackingContextValue,
  event: EventType,
  isPssOddsBoostMarketTypeCodesEnabled: boolean
): TrackingContextValue =>
  mergeWithTrackingContextValue(trackingContext, [
    sportsEvent({
      eventId: event.eventId,
      eventStartTime: new Date(event.startTime).getTime()
    }),
    sportsMarket({
      marketId: event.market?.marketId ?? '',
      marketInPlay: Boolean(event.market?.inPlay),
      marketIsOddsBoost: isOddsBoostMarket(
        isPssOddsBoostMarketTypeCodesEnabled,
        event.market?.marketType
      )
    }),
    sportsCompetition({ competitionId: event.competitionId }),
    sportsSport({ sportId: event.eventTypeId })
  ]);

export interface Props {
  event: EventType;
  numberOfRunners?: number;
}

const Event: React.FC<Props> = ({ event, numberOfRunners = event.market?.runners.length ?? 0 }) => {
  const {
    competitionId,
    competitionSlug,
    eventId,
    eventName,
    eventSlug,
    eventTypeId,
    eventTypeSlug,
    market,
    participants,
    startTime,
    oddsBoostCount,
    hasStarSubEnabledMarkets
  } = event;

  const { formatMessage } = useRegulatoryIntl();
  const trackingContextValue = useContext(TrackingContext);
  const eventStatus = useEventStatus({
    inPlay: market?.inPlay,
    startTime,
    canTurnInPlay: market?.offeredInPlay
  });
  const { widgetSelect } = useEventMarketTemplatesWidget();
  // TODO: remove @typescript-eslint/no-unnecessary-condition once https://jira.pyrsoftware.ca/jira/browse/GSFE-2158 is complete
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const eventType = useSelector((state: RootState) => widgetSelect?.eventType(state, eventTypeId));
  const competition = useSelector((state: RootState) =>
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    widgetSelect?.competition(state, competitionId)
  );
  const isPssOddsBoostMarketTypeCodesEnabled = useFeature(
    FeatureFlag.ISPReleasePssOddsBoostMarketTypeCodes
  );
  const { isError: isScoreboardError } = useLiveData();
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPScoreboards);

  const isValidInPlayStatus = ['IN_PLAY', 'GOING_IN_PLAY'].includes(eventStatus);
  const isScoreboardAvailable =
    isScoreboardsEnabled &&
    getIsEventTypeScoreboardEnabled(eventTypeId) &&
    !isScoreboardError &&
    isValidInPlayStatus;

  return (
    <>
      <TrackingContext.Provider
        value={appendTrackingEntities(
          trackingContextValue,
          event,
          isPssOddsBoostMarketTypeCodesEnabled
        )}
      >
        <div className={styles.container}>
          <div
            className={classNames(styles.event, {
              [styles.twoRunnerMarket]: numberOfRunners === 2,
              [styles.threeRunnerMarket]: numberOfRunners === 3
            })}
          >
            <div className={styles.game}>
              <Link
                className={styles.teams}
                alias="sportsEvent"
                values={{
                  eventTypeSlug,
                  eventTypeId,
                  competitionSlug,
                  competitionId,
                  eventSlug,
                  eventId
                }}
              >
                {isScoreboardAvailable ? (
                  <Scoreboard
                    participants={participants}
                    isGoingInPlay={eventStatus === 'GOING_IN_PLAY'}
                    eventId={eventId}
                    eventTypeId={eventTypeId}
                  />
                ) : (
                  <Participants
                    eventTypeId={eventTypeId}
                    eventName={eventName}
                    participants={participants}
                  />
                )}
              </Link>
            </div>

            {event.market && numberOfRunners > 0 ? (
              <div className={styles.selections}>
                {market?.runners.map((runner) => (
                  <Selection
                    competitionId={competitionId}
                    eventId={eventId}
                    eventTypeId={eventTypeId}
                    handicap={runner.handicap}
                    key={runner.selectionId}
                    legTypes={market.legTypes}
                    marketId={market.marketId}
                    marketStatus={market.marketStatus}
                    marketType={market.marketType}
                    odds={runner.winRunnerOdds}
                    runnerResultType={runner.resultType}
                    runnerStatus={runner.runnerStatus}
                    selectionId={runner.selectionId}
                    startTime={startTime}
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div className={styles.bottomRow}>
            <div className={styles.status}>
              <EventStatus
                inPlay={market?.inPlay}
                startTime={startTime}
                canTurnInPlay={market?.offeredInPlay}
                eventId={eventId}
                eventTypeId={eventTypeId}
              />
            </div>

            {hasStarSubEnabledMarkets ? (
              <Icon
                name="star-sub"
                className={oddsBoostCount ? styles.starSub : undefined}
                label={formatMessage(TXTWCL_Star_Sub_selections)}
                size="lg"
              />
            ) : null}

            {oddsBoostCount ? (
              <Badges
                className={styles.badge}
                count={oddsBoostCount}
                iconName="price-boost"
                iconLabel={formatMessage(TXTWCL_odds_boost_selections)}
              />
            ) : null}

            {!market ? (
              <Link
                className={styles.moreLink}
                alias="sportsEvent"
                values={{
                  eventTypeSlug,
                  eventTypeId,
                  competitionSlug,
                  competitionId,
                  eventSlug,
                  eventId
                }}
              >
                <Translation {...TXTWCL_More_markets} />
                <Icon name="chevron-medium-right" size="lg" />
              </Link>
            ) : null}
          </div>
        </div>
      </TrackingContext.Provider>

      {market && (
        <EventStructuredData
          event={{
            ...event,
            eventStartTime: event.startTime,
            isInPlay: market.inPlay
          }}
          market={{
            ...market,
            eventId,
            eventTypeId,
            marketType: market.marketType || '',
            marketTime: event.startTime,
            isStartingPriceMarket: false
          }}
          eventType={eventType}
          competition={competition}
        />
      )}
    </>
  );
};

export { Event };
